import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CalendarDays, Copy, Info, Users } from "lucide-react";
import { useAuth } from "@/core/hooks/useAuth";
import { ModalSairSemSalvar } from "@/shared/components/ModalSairSemSalvar";
import { useProtecaoSaida } from "@/shared/hooks/useProtecaoSaida";
import { combinarDataHora, dataHojeInput } from "@/shared/lib/datas";
import { CamposEscala, type ValoresEscala } from "@/modules/escala/components/CamposEscala";
import { EtapasCriacaoEscala, type FaseCriacao } from "@/modules/escala/components/EtapasCriacaoEscala";
import { Pill } from "@/modules/escala/components/Pill";
import { SeletorParticipantes } from "@/modules/escala/components/SeletorParticipantes";
import {
  useAdicionarParticipantes,
  useCandidatosParticipantes,
  useCriarEscala,
  useMinisteriosParaCriarEscala,
  useUltimaEquipe,
} from "@/modules/escala/hooks/useEscalas";

type Etapa = "formulario" | FaseCriacao;

function valoresIniciais(): ValoresEscala {
  return { ministerioId: "", nome: "", descricao: "", data: dataHojeInput(), hora: "19:00", exigeConfirmacao: false };
}

function plural(n: number, singular: string, pluralTexto: string): string {
  return `${n} ${n === 1 ? singular : pluralTexto}`;
}

function recorte(texto: string, limite: number): string {
  return texto.length > limite ? `${texto.slice(0, limite - 1)}…` : texto;
}

/**
 * Nova escala já com a equipe (proposta 5 aprovada em 03/10/2026). Duas
 * chamadas em sequência — `escala_criar` e depois
 * `escala_adicionar_participantes` (lote numa transação só); a equipe é
 * opcional. Se a segunda falhar, a escala já existe: a tela diz isso e permite
 * tentar de novo só a inclusão (nunca recria a escala).
 *
 * Fora do escopo desta tela (sem backend que sirva hoje): aviso de conflito de
 * horário na criação — `escala_verificar_conflito` exige uma escala já criada;
 * o aviso continua no "Adicionar participantes" da própria escala.
 */
export function NovaEscalaPage() {
  const navigate = useNavigate();
  const { pessoa } = useAuth();

  const { data: ministerios = [], isLoading: carregandoMinisterios } = useMinisteriosParaCriarEscala(pessoa?.id);
  const criar = useCriarEscala();
  const adicionar = useAdicionarParticipantes();

  const [valoresBase] = useState<ValoresEscala>(valoresIniciais);
  const [valores, setValores] = useState<ValoresEscala>(valoresBase);
  const [selecionados, setSelecionados] = useState<Record<string, string>>({}); // pessoaId -> funcaoId
  const [etapa, setEtapa] = useState<Etapa>("formulario");
  const [escalaCriadaId, setEscalaCriadaId] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [erroEquipe, setErroEquipe] = useState<string | null>(null);

  // Quem lidera um único ministério já o encontra escolhido.
  const ministerioId = valores.ministerioId || (ministerios.length === 1 ? ministerios[0]!.id : "");
  const { data: candidatos, isLoading: carregandoCandidatos } = useCandidatosParticipantes(ministerioId || undefined);
  const { data: ultimaEquipe } = useUltimaEquipe(ministerioId || undefined);

  const lista = candidatos ?? [];
  const itens = Object.entries(selecionados).map(([pessoaId, funcaoId]) => ({ pessoaId, funcaoId }));
  const quantidade = itens.length;
  const voceNaLista = !!pessoa && !!selecionados[pessoa.id];
  const avisados = quantidade - (voceNaLista ? 1 : 0);

  // "Repetir equipe": só entra quem ainda é integrante ativo, na mesma função (o servidor valida de novo).
  const equipeRepetivel = (ultimaEquipe?.itens ?? []).filter((i) =>
    lista.some((c) => c.pessoaId === i.pessoaId && c.funcaoId === i.funcaoId),
  );

  const formularioValido = !!ministerioId && !!valores.nome.trim() && !!valores.data && !!valores.hora;
  const ocupado = etapa !== "formulario";

  // Há o que perder enquanto a escala ainda não foi criada e algo foi preenchido/escolhido.
  const alterado =
    (Object.keys(valoresBase) as (keyof ValoresEscala)[]).some((k) => valores[k] !== valoresBase[k]) ||
    Object.keys(selecionados).length > 0;
  const { pedindoSaida, confirmarSaida, continuarEditando } = useProtecaoSaida(
    alterado && etapa === "formulario" && !escalaCriadaId,
  );

  function atualizar<K extends keyof ValoresEscala>(campo: K, valor: ValoresEscala[K]) {
    setValores((v) => ({ ...v, [campo]: valor }));
    // A equipe pertence ao ministério escolhido: trocar de ministério limpa a seleção.
    if (campo === "ministerioId") setSelecionados({});
  }

  function repetirEquipe() {
    const proxima: Record<string, string> = {};
    for (const i of equipeRepetivel) proxima[i.pessoaId] = i.funcaoId;
    setSelecionados(proxima);
  }

  async function incluirEquipe(escalaId: string) {
    setEtapa("incluindo");
    setErroEquipe(null);
    try {
      await adicionar.mutateAsync({ escalaId, itens });
      setEtapa("concluida");
    } catch (e) {
      setErroEquipe(e instanceof Error ? e.message : "Não foi possível escalar a equipe.");
      setEtapa("falha_equipe");
    }
  }

  async function aoCriar(e: FormEvent) {
    e.preventDefault();
    if (!formularioValido || ocupado) return;
    setErro(null);
    setEtapa("criando");
    let id: string;
    try {
      id = await criar.mutateAsync({
        ministerioId,
        nome: valores.nome.trim(),
        descricao: valores.descricao.trim() ? valores.descricao.trim() : null,
        dataHora: combinarDataHora(valores.data, valores.hora),
        exigeConfirmacao: valores.exigeConfirmacao,
      });
    } catch (err) {
      // Nada foi criado: volta ao formulário com tudo preenchido.
      setErro(err instanceof Error ? err.message : "Não foi possível criar a escala.");
      setEtapa("formulario");
      return;
    }
    setEscalaCriadaId(id);
    if (quantidade === 0) {
      setEtapa("concluida");
      return;
    }
    await incluirEquipe(id);
  }

  function criarOutra() {
    setValores(valoresIniciais());
    setSelecionados({});
    setEscalaCriadaId(null);
    setErro(null);
    setErroEquipe(null);
    setEtapa("formulario");
  }

  const resumoConcluida =
    quantidade === 0
      ? "Você pode escalar a equipe depois, na própria escala."
      : `${plural(quantidade, "pessoa escalada", "pessoas escaladas")}. ${
          valores.exigeConfirmacao ? "Cada uma precisa confirmar a presença." : "Cada uma já entrou confirmada."
        }${avisados > 0 ? ` ${plural(avisados, "pessoa recebe", "pessoas recebem")} a notificação.` : ""}`;

  return (
    <main className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 py-8 sm:px-6 sm:py-10">
      <h1 className="text-2xl font-extrabold text-text">Nova escala</h1>

      <form onSubmit={aoCriar} className="flex flex-col gap-4" noValidate>
        <section aria-labelledby="secao-dados" className="flex flex-col gap-4 rounded-2xl border border-border bg-surface p-4">
          <h2 id="secao-dados" className="flex items-center gap-2 text-[15px] font-extrabold text-text">
            <CalendarDays size={18} aria-hidden className="text-primary" /> Informações da escala
          </h2>
          <CamposEscala
            valores={{ ...valores, ministerioId }}
            onAtualizar={atualizar}
            criacao={{ ministerios, carregando: carregandoMinisterios }}
          />
        </section>

        <section aria-labelledby="secao-equipe" className="flex flex-col gap-3 rounded-2xl border border-border bg-surface p-4">
          <div className="flex flex-col gap-1">
            <h2 id="secao-equipe" className="flex items-center gap-2 text-[15px] font-extrabold text-text">
              <Users size={18} aria-hidden className="text-primary" /> Equipe <Pill tom="off">Opcional</Pill>
            </h2>
            <p className="text-[13px] text-muted">
              Quem você escalar já recebe uma notificação assim que a escala for criada. Dá para escalar mais gente depois.
            </p>
          </div>

          {ministerioId ? (
            <SeletorParticipantes
              candidatos={lista}
              carregando={carregandoCandidatos}
              selecionados={selecionados}
              onMudar={setSelecionados}
              listaRolavel
              atalho={
                ultimaEquipe && equipeRepetivel.length > 0
                  ? {
                      rotulo: `Repetir equipe de “${recorte(ultimaEquipe.escalaNome, 22)}” (${equipeRepetivel.length})`,
                      icone: Copy,
                      onUsar: repetirEquipe,
                    }
                  : undefined
              }
            />
          ) : (
            <p className="rounded-2xl border border-dashed border-border px-4 py-5 text-center text-sm text-muted">
              Escolha o ministério para ver quem pode entrar na equipe.
            </p>
          )}
        </section>

        {quantidade > 0 && (
          <div role="status" className="flex flex-col gap-2 rounded-2xl bg-info/10 px-3.5 py-3 text-[13.5px] leading-snug text-text">
            <p className="flex items-start gap-2.5">
              <Bell size={18} aria-hidden className="mt-px shrink-0 text-info" />
              <span>
                <b>{plural(quantidade, "pessoa", "pessoas")}</b> {quantidade === 1 ? "será escalada" : "serão escaladas"} ao
                criar a escala.{" "}
                {valores.exigeConfirmacao
                  ? "Cada uma precisa confirmar a presença."
                  : "Cada uma entra já confirmada."}
              </span>
            </p>
            {voceNaLista && (
              <p className="flex items-start gap-2.5">
                <Info size={18} aria-hidden className="mt-px shrink-0 text-info" />
                <span>Você está na lista: quem escala a si mesmo não recebe notificação.</span>
              </p>
            )}
          </div>
        )}

        {erro && (
          <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">
            {erro}
          </p>
        )}

        <div className="flex flex-col gap-1.5">
          <button
            type="submit"
            disabled={!formularioValido || ocupado}
            className="min-h-12 w-full rounded-xl bg-primary px-4 text-[15px] font-semibold text-on-primary transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {quantidade > 0 ? `Criar escala e escalar ${plural(quantidade, "pessoa", "pessoas")}` : "Criar escala"}
          </button>
          <p className="min-h-[18px] text-center text-[12.5px] text-muted">
            {formularioValido ? "" : "Escolha o ministério e preencha o nome da escala para continuar."}
          </p>
          <button
            type="button"
            onClick={() => navigate("/escalas")}
            disabled={ocupado}
            className="min-h-12 w-full rounded-xl bg-surface-2 px-4 text-[15px] font-semibold text-text transition-colors hover:bg-border disabled:cursor-not-allowed disabled:opacity-50"
          >
            Voltar
          </button>
        </div>
      </form>

      <ModalSairSemSalvar aberto={pedindoSaida} onSair={confirmarSaida} onContinuar={continuarEditando} />

      {ocupado && (
        <EtapasCriacaoEscala
          fase={etapa}
          quantidade={quantidade}
          resumoConcluida={resumoConcluida}
          erroEquipe={erroEquipe}
          onVerEscala={() => escalaCriadaId && navigate(`/escalas/${escalaCriadaId}`)}
          onCriarOutra={criarOutra}
          onTentarNovamente={() => escalaCriadaId && void incluirEquipe(escalaCriadaId)}
        />
      )}
    </main>
  );
}
